import {
  forwardRef,
  type ChangeEventHandler,
  type SelectHTMLAttributes,
} from 'react';
import { ChevronDown } from 'lucide-react';
import { cn } from '@/lib/utils';

export interface SelectOption {
  value: string;
  label: string;
  disabled?: boolean;
}

interface SelectProps
  extends Omit<SelectHTMLAttributes<HTMLSelectElement>, 'value' | 'onChange'> {
  /** Controlled value of the selected option. */
  value: string;
  /** Native change handler; receives the event (read `e.target.value`). */
  onChange: ChangeEventHandler<HTMLSelectElement>;
  /**
   * Options to render as `<option>` elements. Omit and pass raw `<option>`
   * children instead when the option markup needs more than value/label.
   */
  options?: SelectOption[];
}

/**
 * The one dropdown. Canonical look: token-styled box, one lucide chevron
 * (absolutely positioned, pointer-events-none), one focus ring. Wraps the
 * native `<select>` — option lists, `value`/`onChange`, and a11y behaviour
 * are preserved with zero call-site API change.
 */
const SELECT_CLASS =
  'cursor-pointer appearance-none rounded-md border border-border bg-secondary px-3 py-1.5 pr-8 text-sm text-foreground transition-colors hover:bg-accent focus:outline-none focus:ring-2 focus:ring-ring disabled:cursor-not-allowed disabled:opacity-50';

export const Select = forwardRef<HTMLSelectElement, SelectProps>(function Select(
  { options, className, children, ...props },
  ref,
) {
  return (
    <div className="relative">
      <select ref={ref} className={cn(SELECT_CLASS, className)} {...props}>
        {options?.map((option) => (
          <option key={option.value} value={option.value} disabled={option.disabled}>
            {option.label}
          </option>
        ))}
        {children}
      </select>
      <ChevronDown
        className="pointer-events-none absolute right-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground"
        aria-hidden
      />
    </div>
  );
});
